import axios from 'axios';
import {messageErreur} from '../utils/messageErreur';
import keycloak from '../keycloak';
import {GestionnairesFluxSse, ouvrirFluxSse, rafraichirJeton} from './fluxSse';
import {
    Client,
    ClientOption,
    ConteneurAuParc,
    ConteneurLivrableDTO,
    DashboardExportSummary,
    DecisionDashboard,
    DistributionCarburant,
    DossierExport,
    DossierImport,
    HistoriqueConteneur,
    Livraison,
    LogisticsDashboardSummary,
    AxeRapport,
    DocumentCamion,
    DomaineRapport,
    NotificationApplicative,
    Panne,
    RapportLigne,
    Parc,
    Perimetre,
    PieceRechange,
    Fournisseur,
    PlanifierRecuperationRequest,
    PrixStation,
    RapportMensuelCarburant,
    Recuperation,
    StatutPanne,
    TypePanneReference,
    Transitaire,
    TransitaireOption,
    Ville,
    ZoneCarburant,
    Utilisateur,
    CreateUserRequest,
    UpdateUserRequest,
    ChangePasswordRequest,
    UserLog,
    LivraisonWorkflow,
    StatsChauffeur,
    TrackingGPS,
    PositionActiveChauffeur,
    PositionConteneur,
    FiltresLivraison,
    ApiResponse,
    PhotoLivraison,
    Remorque,
    ConteneurExport
} from '../types';

interface Chauffeur {
    id?: number;
    /** Identifiant metier stable — CH-001. Genere par la base, immuable. */
    codeChauffeur?: string;
    nom: string;
    telephone?: string;
    keycloakId?: string;
    numPermis?: string;
    actif: boolean;
    createdAt?: string;
    updatedAt?: string;
    motDePasseTemporaire?: string;
    /** Pourquoi ce chauffeur n a pas de compte de connexion — absent s il en a un. */
    avertissementCompte?: string;
}

interface Camion {
    id?: number;
    numero: string;
    marque?: string;
    modele?: string;
    immatriculation?: string;
    actif: boolean;
    createdAt?: string;
    updatedAt?: string;
}

// Interfaces pour les notifications chauffeur
export interface NotificationChauffeur {
    id: number;
    livraisonId: number;
    chauffeurTelephone: string;
    chauffeurNom: string;
    statut: 'ATTENTE' | 'ACCEPTER' | 'EN_ROUTE' | 'LIVRE' | 'VALIDEE';
    numConteneur?: string;
    booking?: string;
    numBl?: string;
    nomClient?: string;
    adresseLivraison?: string;
    dateLivraisonPrevue?: string;
    dateCreation?: string;
    dateAcceptation?: string;
    dateEnRoute?: string;
    dateLivraison?: string;
    dateValidation?: string;
    coordonneesGPS?: string;
    remarque?: string;
    utilisateurValidation?: string;
    photoEir?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface UpdateNotificationStatutRequest {
    statut: 'ACCEPTER' | 'EN_ROUTE' | 'DEPOSE_PARC' | 'LIVRE';
    telephoneChauffeur: string;
    coordonneesGPS?: string;
    remarque?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        // 'Content-Type': 'application/json',
        // 'Accept': 'application/json',
        // 'Access-Control-Allow-Origin': '*',
        // 'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
        // 'Access-Control-Allow-Headers': 'Content-Type, Authorization',
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    timeout: 60000, // 15 secondes de timeout
    withCredentials: false, // Set to true if using cookies/sessions
});

// Intercepteur pour les requêtes (rafraîchit le token Keycloak automatiquement)
api.interceptors.request.use(
    async (config) => {
        config.headers.set('X-Requested-With', 'XMLHttpRequest');
        config.headers.set('Cache-Control', 'no-cache');
        config.headers.set('Pragma', 'no-cache');

        if (keycloak.authenticated) {
            try {
                await rafraichirJeton();
                config.headers.set('Authorization', `Bearer ${keycloak.token}`);
            } catch (err) {
                console.error('Erreur refresh token Keycloak:', err);
                keycloak.logout({ redirectUri: window.location.origin });
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Intercepteur pour les réponses
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        // Le message du serveur, quelle que soit la forme de son corps (2026-09-23).
        // L'extraction se faisait ici à la main et ignorait la CHAÎNE NUE, renvoyée par seize
        // contrôleurs : on retombait alors sur error.message, c'est-à-dire « Request failed with
        // status code 400 » — un message qui n'apprend rien à l'opérateur. Voir utils/messageErreur.
        const errorMessage = error.response?.headers?.['x-error-message'] ||
            messageErreur(error, '') ||
            error.message;

        // Gestion spécifique des erreurs
        if (error.response?.status === 403) {
            error.userMessage = errorMessage || 'Accès interdit. Vérifiez vos permissions.';
        } else if (error.response?.status === 404) {
            error.userMessage = 'Ressource non trouvée.';
        } else if (error.response?.status === 400) {
            error.userMessage = errorMessage || 'Données invalides.';
        } else if (error.response?.status === 500) {
            error.userMessage = errorMessage || 'Erreur serveur interne.';
        } else if (error.code === 'ECONNABORTED') {
            error.userMessage = 'La requête a pris trop de temps. Vérifiez votre connexion.';
        } else if (!error.response) {
            error.userMessage = 'Impossible de joindre le serveur. Vérifiez votre connexion.';
        } else {
            error.userMessage = errorMessage || 'Une erreur inattendue s\'est produite.';
        }

        return Promise.reject(error);
    }
);

// Dossiers Import
export const dossierImportAPI = {
    getAll: () => api.get<DossierImport[]>('/dossiers-import/all'),
    /**
     * Fiche PDF d'un dossier, generee par le backend.
     *
     * Remplace l'impression navigateur : la fiche etant remise a un tiers, elle doit etre
     * identique quel que soit le poste qui l'edite. Aucun reglage @page ne peut le garantir.
     */
    fichePdf: (id: number) =>
        api.get(`/dossiers-import/${id}/fiche.pdf`, {responseType: 'blob'}),
    /** Impression groupée : les fiches de la période, mêmes filtres que la liste (2026-10-21). */
    fichesPdf: (filtres: Record<string, string | number | undefined>) =>
        api.get('/dossiers-import/fiches.pdf', {params: filtres, responseType: 'blob'}),
    /**
     * Liste paginée. Les filtres date/armateur/terminal sont envoyés au serveur : les appliquer
     * côté client après pagination laissait des pages vides et un nombre de pages faux.
     */
    getAllPaginated: (page: number = 0, size: number = 10, sortBy: string = 'idDossier', sortDir: string = 'desc',
                      search?: string, filtres?: { dateDebut?: string; dateFin?: string; armateur?: string; terminalId?: string }) =>
        api.get('/dossiers-import', {
            params: {
                page, size, sortBy, sortDir,
                ...(search ? { search } : {}),
                ...(filtres?.dateDebut ? { dateDebut: filtres.dateDebut } : {}),
                ...(filtres?.dateFin ? { dateFin: filtres.dateFin } : {}),
                ...(filtres?.armateur ? { armateur: filtres.armateur } : {}),
                ...(filtres?.terminalId ? { terminalId: filtres.terminalId } : {}),
            },
        }),
    getById: (id: number) => api.get<DossierImport>(`/dossiers-import/${id}`),
    getByBl: (bl: string) => api.get<DossierImport>(`/dossiers-import/bl/${bl}`),
    create: (dossier: DossierImport) => api.post<DossierImport>('/dossiers-import', dossier),
    update: (id: number, dossier: DossierImport) => api.put<DossierImport>(`/dossiers-import/${id}`, dossier),
    delete: (id: number) => api.delete(`/dossiers-import/${id}`),
    getByZone: (zone: string) => api.get<DossierImport[]>(`/dossiers-import/zone/${zone}`),
    getExpiredBadt: () => api.get<DossierImport[]>('/dossiers-import/expired-badt'),
    /**
     * Dossiers dont la BADT peut encore produire une alerte, bornés côté serveur.
     * Remplace, pour le bandeau d'alertes, le chargement des 50 derniers dossiers — au-delà
     * duquel une BADT expirant le jour même ne déclenchait rien.
     */
    getBadtASurveiller: (joursAvant = 30) =>
        api.get<DossierImport[]>('/dossiers-import/badt-a-surveiller', { params: { joursAvant } }),
    getAllBLNumbers: () => api.get<string[]>('/dossiers-import/bl-numbers'),
    getConteneursByBL: (bl: string) => api.get<string[]>(`/dossiers-import/conteneurs/${bl}`),
    getStats: () => api.get('/dossiers-import/stats'),

    // Exports — fetch() brut sans Authorization abandonné : ce endpoint est protégé par
    // Keycloak, et credentials:'omit' + aucun header Authorization renvoyait 401 quel que
    // soit l'utilisateur connecté. L'instance axios "api" attache le token via son
    // intercepteur (cf. api.interceptors.request), donc réutilisée ici comme partout ailleurs.
    exportExcel: () => api.get('/dossiers-import/export/excel', {
        responseType: 'blob',
        headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'X-Requested-With': 'XMLHttpRequest',
        },
        timeout: 30000
    }),

    // Méthodes de fallback avec axios
    exportExcelAxios: () => api.get('/dossiers-import/export/excel', {
        responseType: 'blob',
        headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'X-Requested-With': 'XMLHttpRequest',
        },
        timeout: 30000
    }),


    // Exports avec paramètres de filtre
    exportExcelWithParams: (params: string) => {
        return fetch(`${API_BASE_URL}/dossiers-import/export/excel?${params}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            mode: 'cors',
            credentials: 'omit'
        }).then(response => {
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return response;
        });
    },

};

// Dossiers Export
export const dossierExportAPI = {
    /**
     * Fiche PDF d'un dossier export, generee par le backend.
     *
     * Remplace l'impression navigateur : la fiche etant remise a un tiers, elle doit etre
     * identique quel que soit le poste qui l'edite.
     */
    fichePdf: (id: number) =>
        api.get(`/dossiers-export/${id}/fiche.pdf`, {responseType: 'blob'}),
    /** Impression groupée : les fiches de la période, mêmes filtres que la liste (2026-10-21). */
    fichesPdf: (filtres: Record<string, string | number | undefined>) =>
        api.get('/dossiers-export/fiches.pdf', {params: filtres, responseType: 'blob'}),
    getAll: () => api.get<DossierExport[]>('/dossiers-export'),
    /**
     * Liste paginée. Les filtres date/armateur/terminal sont envoyés au serveur : les appliquer
     * côté client après pagination laissait des pages vides et un nombre de pages faux (même
     * correctif que dossierImportAPI.getAllPaginated).
     */
    getPage: (page: number = 0, size: number = 25, search?: string,
              filtres?: { dateDebut?: string; dateFin?: string; armateur?: string; terminalId?: string }) =>
        api.get('/dossiers-export/page', {
            params: {
                page, size,
                ...(search ? { search } : {}),
                ...(filtres?.dateDebut ? { dateDebut: filtres.dateDebut } : {}),
                ...(filtres?.dateFin ? { dateFin: filtres.dateFin } : {}),
                ...(filtres?.armateur ? { armateur: filtres.armateur } : {}),
                ...(filtres?.terminalId ? { terminalId: filtres.terminalId } : {}),
            },
        }),
    /**
     * Comptages des cartes de Gestion des Exports — dossiers au total, conteneurs par étape.
     *
     * Reçoit les MÊMES filtres que getPage : sans eux, l'écran affichait une liste filtrée à
     * côté de cartes comptant toute la base.
     */
    getStats: (search?: string, filtres?: { dateDebut?: string; dateFin?: string; armateur?: string; terminalId?: string }) =>
        api.get<Record<string, number>>('/dossiers-export/stats', {
            params: {
                ...(search ? { search } : {}),
                ...(filtres?.dateDebut ? { dateDebut: filtres.dateDebut } : {}),
                ...(filtres?.dateFin ? { dateFin: filtres.dateFin } : {}),
                ...(filtres?.armateur ? { armateur: filtres.armateur } : {}),
                ...(filtres?.terminalId ? { terminalId: filtres.terminalId } : {}),
            },
        }),
    getById: (id: number) => api.get<DossierExport>(`/dossiers-export/${id}`),
    create: (body: any) => api.post<DossierExport>('/dossiers-export', body),
    update: (id: number, dossier: DossierExport) => api.put<DossierExport>(`/dossiers-export/${id}`, dossier),
    delete: (id: number) => api.delete(`/dossiers-export/${id}`),

    /** Positionnement d'un conteneur dont le numéro est saisi maintenant (2026-10-30). */
    positionnerConteneur: (idDossier: number, data: Record<string, string | number | boolean | undefined>) =>
        api.post<ConteneurExport>(`/dossiers-export/${idDossier}/positionner-conteneur`, data),
    /** Relevage seul : le numéro du conteneur se saisit au relevage (2026-10-31). */
    releverConteneur: (idDossier: number, data: Record<string, string | number | boolean | undefined>) =>
        api.post<ConteneurExport>(`/dossiers-export/${idDossier}/relever-conteneur`, data),
    modifierPositionnement: (conteneurId: number, data: Record<string, string | number | undefined>) =>
        api.put<ConteneurExport>(`/dossiers-export/conteneur/${conteneurId}/positionnement`, data),
    modifierRelevage: (conteneurId: number, data: Record<string, string | number | undefined>) =>
        api.put<ConteneurExport>(`/dossiers-export/conteneur/${conteneurId}/relevage`, data),
    positionner: (conteneurId: number, data: {
        chauffeur: string;
        /** Designe le chauffeur ; fait foi cote serveur, le nom n est qu un affichage. */
        chauffeurId?: number;
        camion: string;
        remorque?: string;
        remarque?: string;
        datePositionnement?: string;
        typeMission?: string;
    }) => api.post(`/dossiers-export/conteneur/${conteneurId}/positionner`, data),

    relever: (conteneurId: number, data: {
        chauffeur: string;
        /** Designe le chauffeur ; fait foi cote serveur, le nom n est qu un affichage. */
        chauffeurId?: number;
        camion: string;
        remorque?: string;
        remarque?: string;
        dateRelevage?: string;
        destinationRelevage?: string;
        blRelevage?: string;
        typeMission?: string;
    }) => api.post(`/dossiers-export/conteneur/${conteneurId}/relever`, data),

    getConteneursByEtape: (etape: string) =>
        api.get(`/dossiers-export/conteneurs`, { params: { etape } }),

    // Exports avec méthodes alternatives
    exportExcel: () => {
        return fetch(`${API_BASE_URL}/dossiers-export/export/excel`, {
            method: 'GET',
            headers: {
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            mode: 'cors',
            credentials: 'omit'
        }).then(response => {
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return response;
        });
    },


    // Méthodes de fallback
    exportExcelAxios: () => api.get('/dossiers-export/export/excel', {
        responseType: 'blob',
        headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'X-Requested-With': 'XMLHttpRequest',
        },
        timeout: 30000
    }),

};

// Livraisons
export const livraisonAPI = {
    getAll: () => api.get<Livraison[]>('/livraisons'),
    getPage: (page: number = 0, size: number = 25, search?: string, status?: string, type?: string, dateDebut?: string, dateFin?: string, signal?: AbortSignal) =>
        api.get('/livraisons/page', { params: { page, size, ...(search ? { search } : {}), ...(status && status !== 'all' ? { status } : {}), ...(type && type !== 'all' ? { type } : {}), ...(dateDebut ? { dateDebut } : {}), ...(dateFin ? { dateFin } : {}) }, signal }),
    getKpi: () => api.get<Record<string, number>>('/livraisons/kpi'),
    /**
     * Comptages des cartes de Gestion des Livraisons.
     *
     * Reçoit les MÊMES filtres que getPage : sans eux, l écran affichait une liste filtrée à
     * côté de cartes comptant toute la base — deux chiffres sur des périmètres différents.
     */
    getStats: (search?: string, status?: string, type?: string, dateDebut?: string, dateFin?: string) =>
        api.get<Record<string, number>>('/livraisons/stats', {
            params: { ...(search ? { search } : {}), ...(status && status !== 'all' ? { status } : {}), ...(type && type !== 'all' ? { type } : {}), ...(dateDebut ? { dateDebut } : {}), ...(dateFin ? { dateFin } : {}) },
        }),
    exportExcel: (search?: string, status?: string, type?: string, dateDebut?: string, dateFin?: string) =>
        api.get('/livraisons/export/excel', {
            params: { ...(search ? { search } : {}), ...(status && status !== 'all' ? { status } : {}), ...(type && type !== 'all' ? { type } : {}), ...(dateDebut ? { dateDebut } : {}), ...(dateFin ? { dateFin } : {}) },
            responseType: 'blob'
        }),
    // Sprint 3bis : variante bornée dans le temps pour le Dashboard (évite de rapatrier
    // toute la table juste pour des agrégations qui ne couvrent que les ~12-13 derniers mois)
    getRecentes: (mois: number = 13) => api.get<Livraison[]>('/livraisons/recentes', { params: { mois } }),
    getById: (id: number) => api.get<ApiResponse<LivraisonWorkflow>>(`/livraisons/${id}`),
    getLivraisonsTerminees: () => api.get<Livraison[]>('/livraisons/terminees'),
    /**
     * Livraisons d'un seul conteneur — quelques lignes au plus (un conteneur enchaîne au
     * maximum un DEPOT_PARC puis une SORTIE_PARC). Remplace, pour les contrôles de doublon
     * de la modale de livraison, le rapatriement de toute la table : les trois vérifications
     * concernées ne regardent de toute façon que les lignes de ce conteneur.
     */
    getByConteneur: (conteneur: string) =>
        api.get<Livraison[]>(`/livraisons/conteneur/${encodeURIComponent(conteneur)}`),
    /**
     * Autocomplétion sur conteneur, bornée, parmi les livraisons LIVRE ou TERMINEE. Remplace,
     * pour RecuperationModal, le chargement de getLivraisonsTerminees() suivi d'un filtrage en
     * mémoire sur la saisie de l'utilisateur.
     */
    rechercherTermineesParConteneur: (q: string, limit: number = 20) =>
        api.get<Livraison[]>('/livraisons/terminees/recherche', { params: { q, limit } }),
    create: (livraison: Livraison) => api.post<Livraison>('/livraisons', livraison),
    update: (id: number, livraison: Livraison) => api.put<Livraison>(`/livraisons/${id}`, livraison),
    updateStatus: (id: number, status: string) => api.put<Livraison>(`/livraisons/${id}/status/${status}`),
    delete: (id: number) => api.delete(`/livraisons/${id}`),
    marquerTerminee: (id: number) => api.post<Livraison>(`/livraisons/${id}/marquer-terminee`),
    getBLLivres: () => api.get<string[]>('/livraisons/bl-livres'),
    getConteneursLivres: (bl: string) => api.get<string[]>(`/livraisons/conteneurs-livres/${bl}`),
    getConteneursDisponibles: (bl: string) => api.get<string[]>(`/livraisons/conteneurs-disponibles/${bl}`),
    // Vérifier si une livraison existe déjà pour un conteneur
    verifierExistence: (numeroConteneur: string, typeLivraison?: string) => 
        api.get<VerificationLivraisonDTO>('/livraisons/verifier-existence', {
            params: { numeroConteneur, typeLivraison }
        }),
    // ========== NOUVEAUX ENDPOINTS POUR VALIDATION AGENT DE CONTRÔLE ==========
    // Valider une livraison (agent de contrôle)
    valider: (id: number, agentNom: string, commentaire?: string) => 
        api.post<Livraison>(`/livraisons/${id}/valider`, { agentNom, commentaire }),
    // Récupérer les livraisons en attente de validation
    getAValider: () => api.get<Livraison[]>('/livraisons/a-valider'),
    // Confirmer le retour physique de l'EIR au bureau — distinct du passage à LIVRE par le
    // chauffeur (bouton "Terminer"), qui ne dit rien du retour du document.
    confirmerEir: (id: number, utilisateur: string, dateRetour?: string) =>
        api.put<Livraison>(`/livraisons/${id}/confirmer-eir`, { utilisateur, dateRetour }),
    // Récupérer les livraisons pour le récapitulatif (filtrées selon règles métier)
    getRecap: () => api.get<Livraison[]>('/livraisons/recap'),
    getRecapPage: (page: number = 0, size: number = 25, dateDebut?: string, dateFin?: string, armateur?: string, search?: string) =>
        api.get('/livraisons/recap/page', { params: { page, size, ...(dateDebut ? { dateDebut } : {}), ...(dateFin ? { dateFin } : {}), ...(armateur && armateur !== 'TOUS' ? { armateur } : {}), ...(search && search.trim() ? { search: search.trim() } : {}) } }),
    
    // ========== ENDPOINTS WORKFLOW CHAUFFEUR ==========
    // Accepter une livraison
    accepter: (id: number) => api.post<ApiResponse<LivraisonWorkflow>>(`/livraisons/${id}/accepter`),
    // Refuser une livraison
    refuser: (id: number, motif: string) => api.post<ApiResponse<LivraisonWorkflow>>(`/livraisons/${id}/refuser`, { motif }),
    // Marquer comme en route
    enRoute: (id: number, position?: { latitude: number; longitude: number }) => 
        api.post<ApiResponse<LivraisonWorkflow>>(`/livraisons/${id}/en-route`, position),
    // Marquer comme arrivé
    arrive: (id: number, position?: { latitude: number; longitude: number }) => 
        api.post<ApiResponse<LivraisonWorkflow>>(`/livraisons/${id}/arrive`, position),
    // Commencer la livraison
    commencerLivraison: (id: number) => api.post<ApiResponse<LivraisonWorkflow>>(`/livraisons/${id}/commencer-livraison`),
    // Marquer comme livré
    livrer: (id: number, data: { remarque?: string; photos?: PhotoLivraison[]; signature?: string }) => 
        api.post<ApiResponse<LivraisonWorkflow>>(`/livraisons/${id}/livrer`, data),
    // Terminer la livraison
    terminer: (id: number) => api.post<ApiResponse<LivraisonWorkflow>>(`/livraisons/${id}/terminer`),
    // Upload de photo
    uploadPhoto: (id: number, file: File, type: PhotoLivraison['type']) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        return api.post<ApiResponse<string>>(`/livraisons/${id}/photos/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

// Interface pour la réponse de vérification de livraison
export interface VerificationLivraisonDTO {
    livraisonExistante: boolean;
    message: string;
    livraisonExistanteDetails?: Livraison;
}

// Parcs
export const parcAPI = {
    getAll: () => api.get<Parc[]>('/parcs'),
    create: (parc: Parc) => api.post<Parc>('/parcs', parc),
    update: (id: number, parc: Parc) => api.put<Parc>(`/parcs/${id}`, parc),
    delete: (id: number) => api.delete(`/parcs/${id}`),
    getByType: (type: string) => api.get<Parc[]>(`/parcs/type/${type}`),
    getDisponibles: () => api.get<Parc[]>('/parcs/disponibles'),
};

// Armateurs — table dynamique gérée depuis l'UI
export const armateurAPI = {
    getActifs: () => api.get<{id: number; code: string; nom: string; actif: boolean}[]>('/armateurs'),
    getAll:    () => api.get<{id: number; code: string; nom: string; actif: boolean}[]>('/armateurs/all'),
    create:    (a: {code: string; nom?: string}) => api.post('/armateurs', a),
    update:    (id: number, a: {code?: string; nom?: string; actif?: boolean}) => api.put(`/armateurs/${id}`, a),
    delete:    (id: number) => api.delete(`/armateurs/${id}`),
};

// Clients - CRUD complet avec suppression (mise à jour)
// perimetre: filtre l'autocomplétion des formulaires (IMPORT/EXPORT) ; omis = liste complète (admin)
// search: recherche par préfixe côté serveur (utilisée par SelectOrAddInput), plafonnée à 20 résultats
export const clientAPI = {
    /**
     * Deux charges utiles selon l'appel, à l'image du backend :
     *  - sans périmètre : Client complet (téléphone, e-mail) — écran de gestion des clients ;
     *  - avec périmètre : ClientOption réduit (id, nom) — listes déroulantes de saisie.
     *
     * Les surcharges reflètent ce contrat pour que TypeScript refuse une lecture de
     * `telephone` sur le chemin réduit, plutôt que de la laisser échouer silencieusement à
     * l'exécution avec un `undefined`.
     */
    getAll: ((perimetre?: Perimetre, search?: string) =>
        api.get('/clients', {params: perimetre ? {perimetre, ...(search ? {search} : {})} : {}})) as {
        (): Promise<{ data: Client[] }>;
        (perimetre: Perimetre, search?: string): Promise<{ data: ClientOption[] }>;
    },
    create: (client: Client) => api.post<Client>('/clients', client),
    update: (id: number, client: Client) => api.put<Client>(`/clients/${id}`, client),
    delete: (id: number) => api.delete(`/clients/${id}`),
};

// Transitaires - CRUD complet avec suppression (mise à jour)
export const transitaireAPI = {
    /** Voir clientAPI.getAll — même contrat à deux charges utiles. */
    getAll: ((perimetre?: Perimetre, search?: string) =>
        api.get('/transitaires', {params: perimetre ? {perimetre, ...(search ? {search} : {})} : {}})) as {
        (): Promise<{ data: Transitaire[] }>;
        (perimetre: Perimetre, search?: string): Promise<{ data: TransitaireOption[] }>;
    },
    create: (transitaire: Transitaire) => api.post<Transitaire>('/transitaires', transitaire),
    update: (id: number, transitaire: Transitaire) => api.put<Transitaire>(`/transitaires/${id}`, transitaire),
    delete: (id: number) => api.delete(`/transitaires/${id}`),
};

// Récupérations
export const recuperationAPI = {
    getAll: () => api.get<Recuperation[]>('/recuperations'),
    // La période (du/au, au format AAAA-MM-JJ) borne les cartes EXACTEMENT comme la liste :
    // deux périmètres côte à côte sur le même écran est un défaut déjà corrigé ici une fois.
    getStats: (du?: string, au?: string) =>
        api.get<Record<string, number>>('/recuperations/stats', {
            params: { ...(du ? { du } : {}), ...(au ? { au } : {}) }
        }),
    // Le tri part au SERVEUR : trié sur la page reçue, « Date ↓ » ne réordonnait que les
    // 25 lignes affichées. Les valeurs de « tri » sont filtrées par une liste blanche côté
    // serveur — une propriété inconnue y lèverait une exception, donc un 500.
    getPage: (page: number = 0, size: number = 25, search?: string, status?: string,
              du?: string, au?: string, tri?: string, sens?: string) =>
        api.get('/recuperations/page', { params: { page, size,
            ...(search ? { search } : {}),
            ...(status && status !== 'all' ? { status } : {}),
            ...(du ? { du } : {}), ...(au ? { au } : {}),
            ...(tri ? { tri } : {}), ...(sens ? { sens } : {}) } }),
    // Sprint 3bis : variante bornée dans le temps pour le Dashboard (cf. livraisonAPI.getRecentes)
    getRecentes: (mois: number = 13) => api.get<Recuperation[]>('/recuperations/recentes', { params: { mois } }),
    create: (recuperation: Recuperation) => api.post<Recuperation>('/recuperations', recuperation),
    update: (id: number, recuperation: Recuperation) => api.put<Recuperation>(`/recuperations/${id}`, recuperation),
    delete: (id: number) => api.delete(`/recuperations/${id}`),
    getMessages: () => api.get<Recuperation[]>('/recuperations/messages'),
    getUrgentes: () => api.get<Recuperation[]>('/recuperations/urgentes'),
    /**
     * Récupérations d'un seul conteneur — quelques lignes au plus. Remplace, pour le contrôle
     * de doublon de la modale de récupération, le rapatriement de toute la table : la
     * vérification ne porte que sur la paire (BL, conteneur) saisie.
     */
    getByConteneur: (numConteneur: string) =>
        api.get<Recuperation[]>(`/recuperations/conteneur/${encodeURIComponent(numConteneur)}`),
    planifier: (id: number, payload: PlanifierRecuperationRequest) =>
        api.post<Recuperation>(`/recuperations/${id}/planifier`, payload),
    // Créer une notification pour le chauffeur lors d'une récupération planifiée
    // chauffeurId fait foi cote serveur depuis la phase 3A : c'est lui qui decide vers quel
    // telephone part la notification Push, et donc qui recoit l'ordre de recuperation.
    createNotification: (recuperationId: number, data: { chauffeur: string; chauffeurId?: number; type: string }) =>
        api.post(`/recuperations/${recuperationId}/notifications`, data),
};

// Cartographie de flotte — position actuelle des chauffeurs en mission
export const trackingGPSAPI = {
    getPositionsActives: () => api.get<PositionActiveChauffeur[]>('/tracking-gps/positions-actives'),
    getPositionsARecuperer: () => api.get<PositionConteneur[]>('/tracking-gps/positions-tc-a-recuperer'),
};

// Dashboard
export const dashboardAPI = {
    getData: () => api.get<LogisticsDashboardSummary>('/dashboard'),
    getLogisticsData: () => api.get<LogisticsDashboardSummary>('/dashboard/logistics'),
    getDecisionDashboard: () => api.get<DecisionDashboard>('/dashboard/decision'),
    getMonthlyStats: () => api.get('/dashboard/monthly-stats'),
    getStatsSummary: (annee?: number) => api.get('/dashboard/stats-summary', { params: annee ? { annee } : {} }),
    getExportSummary: (annee?: number) => api.get<DashboardExportSummary>('/dashboard/export-summary', { params: annee ? { annee } : {} }),
    getArmateurStats: () => api.get('/dashboard/armateur-stats'),
    getArmateurMonthlyStats: (months: number = 6) =>
        api.get('/dashboard/armateur-monthly-stats', {params: {months}}),
    getModeLivraisonStats: () => api.get('/dashboard/mode-livraison-stats'),
    getUrgentDossiers: () => api.get('/dashboard/urgent-dossiers'),
    getDangerConteneurs: () => api.get('/dashboard/danger-conteneurs'),
    getLivraisonsTerminees: () => api.get('/dashboard/livraisons-terminees'),
};

// Authentication (gestion des comptes — l'authentification elle-même est déléguée à Keycloak)
export const authAPI = {
    getUsers: () => api.get('/auth/users'),
    getUserById: (id: number) => api.get(`/auth/users/${id}`),
    updateUser: (id: number, userData: any) => api.put(`/auth/users/${id}`, userData),
    deleteUser: (id: number) => api.delete(`/auth/users/${id}`),
};

// Chauffeurs
export const chauffeurAPI = {
    getAll: () => api.get<Chauffeur[]>('/chauffeurs'),
    getActifs: () => api.get<Chauffeur[]>('/chauffeurs/actifs'),
    getById: (id: number) => api.get<Chauffeur>(`/chauffeurs/${id}`),
    create: (chauffeur: Chauffeur) => api.post<Chauffeur>('/chauffeurs', chauffeur),
    update: (id: number, chauffeur: Chauffeur) => api.put<Chauffeur>(`/chauffeurs/${id}`, chauffeur),
    delete: (id: number) => api.delete(`/chauffeurs/${id}`),
};

export const camionAPI = {
    getAll: () => api.get<Camion[]>("/camions"),
    getActifs: () => api.get<Camion[]>("/camions/actifs"),
    getById: (id: number) => api.get<Camion>(`/camions/${id}`),
    create: (camion: Camion) => api.post<Camion>("/camions", camion),
    update: (id: number, camion: Camion) => api.put<Camion>(`/camions/${id}`, camion),
    delete: (id: number) => api.delete(`/camions/${id}`),
};

export const remorqueAPI = {
    getAll: () => api.get<Remorque[]>("/remorques"),
    getById: (id: number) => api.get<Remorque>(`/remorques/${id}`),
    create: (remorque: Remorque) => api.post<Remorque>("/remorques", remorque),
    update: (id: number, remorque: Remorque) => api.put<Remorque>(`/remorques/${id}`, remorque),
    delete: (id: number) => api.delete(`/remorques/${id}`),
    atteler: (id: number, camionId: number) => api.put<Remorque>(`/remorques/${id}/atteler/${camionId}`),
    deteler: (id: number) => api.put<Remorque>(`/remorques/${id}/deteler`),
};

// Carburant — Zones
export const zoneCarburantAPI = {
    getAll: () => api.get<ZoneCarburant[]>('/zones-carburant'),
    create: (zone: ZoneCarburant) => api.post<ZoneCarburant>('/zones-carburant', zone),
    update: (id: number, zone: ZoneCarburant) => api.put<ZoneCarburant>(`/zones-carburant/${id}`, zone),
    delete: (id: number) => api.delete(`/zones-carburant/${id}`),
};

// Carburant — Villes (litrage standard par ville, rattachée à une zone)
export const villeAPI = {
    getAll: () => api.get<Ville[]>('/villes'),
    getActives: () => api.get<Ville[]>('/villes/actives'),
    create: (ville: Ville) => api.post<Ville>('/villes', ville),
    update: (id: number, ville: Ville) => api.put<Ville>(`/villes/${id}`, ville),
    delete: (id: number) => api.delete(`/villes/${id}`),
};

// Carburant — Prix par station
export const prixStationAPI = {
    getAll: () => api.get<PrixStation[]>('/prix-stations'),
    getActives: () => api.get<PrixStation[]>('/prix-stations/actives'),
    create: (station: PrixStation) => api.post<PrixStation>('/prix-stations', station),
    update: (id: number, station: PrixStation) => api.put<PrixStation>(`/prix-stations/${id}`, station),
    delete: (id: number) => api.delete(`/prix-stations/${id}`),
};

// Carburant — Distributions quotidiennes + rapports
/** Les saisies d'une période, filtrées et paginées par le serveur (2026-10-28). */
export interface SaisiesCarburant {
    content: DistributionCarburant[];
    page: number; totalItems: number; totalPages: number;
    totalLitres: number; totalMontant: number;
}
export const distributionCarburantAPI = {
    getAll: () => api.get<DistributionCarburant[]>('/distributions-carburant'),
    /** @param page à partir de 0 */
    saisies: (debut: string, fin: string, recherche?: string, page = 0, size = 10) =>
        api.get<SaisiesCarburant>('/distributions-carburant/saisies',
            {params: {debut, fin, recherche: recherche || undefined, page, size}}),
    create: (d: DistributionCarburant) => api.post<DistributionCarburant>('/distributions-carburant', d),
    update: (id: number, d: DistributionCarburant) => api.put<DistributionCarburant>(`/distributions-carburant/${id}`, d),
    delete: (id: number) => api.delete(`/distributions-carburant/${id}`),
    // stationId omis = toutes les stations. On n'envoie pas la clé du tout plutôt que
    // stationId=null : axios sérialiserait la chaîne "null", que Spring refuserait de convertir
    // en Long — le paramètre absent est le seul moyen de dire « pas de filtre ».
    rapportPeriodePdf: (debut: string, fin: string, stationId?: number, parCamion = false) =>
        api.get(`/distributions-carburant/rapport-periode`, {
            params: stationId ? {debut, fin, stationId, parCamion} : {debut, fin, parCamion},
            responseType: 'blob',
        }),
    /** Le même rapport en Excel, regroupement par camion facultatif (2026-10-21). */
    rapportPeriodeExcel: (debut: string, fin: string, stationId?: number, parCamion = false) =>
        api.get(`/distributions-carburant/rapport-periode.xlsx`, {
            params: stationId ? {debut, fin, stationId, parCamion} : {debut, fin, parCamion},
            responseType: 'blob',
        }),
    rapportMensuel: (annee: number, mois: number) =>
        api.get<RapportMensuelCarburant>('/distributions-carburant/rapport-mensuel', {params: {annee, mois}}),
};

// Pannes — Catalogue types de panne connus
export const typePanneAPI = {
    getAll: () => api.get<TypePanneReference[]>('/types-panne'),
    getActifs: () => api.get<TypePanneReference[]>('/types-panne/actifs'),
    create: (t: TypePanneReference) => api.post<TypePanneReference>('/types-panne', t),
    update: (id: number, t: TypePanneReference) => api.put<TypePanneReference>(`/types-panne/${id}`, t),
    delete: (id: number) => api.delete(`/types-panne/${id}`),
};

// Pannes — Catalogue fournisseurs
export const fournisseurAPI = {
    getAll: () => api.get<Fournisseur[]>('/fournisseurs'),
    getActifs: () => api.get<Fournisseur[]>('/fournisseurs/actifs'),
    create: (f: Fournisseur) => api.post<Fournisseur>('/fournisseurs', f),
    update: (id: number, f: Fournisseur) => api.put<Fournisseur>(`/fournisseurs/${id}`, f),
    delete: (id: number) => api.delete(`/fournisseurs/${id}`),
};

// Pannes — Catalogue pièces de rechange
export const pieceRechangeAPI = {
    getAll: () => api.get<PieceRechange[]>('/pieces-rechange'),
    getActives: () => api.get<PieceRechange[]>('/pieces-rechange/actives'),
    create: (p: PieceRechange) => api.post<PieceRechange>('/pieces-rechange', p),
    update: (id: number, p: PieceRechange) => api.put<PieceRechange>(`/pieces-rechange/${id}`, p),
    delete: (id: number) => api.delete(`/pieces-rechange/${id}`),
};

// Pannes — Déclaration et suivi
export const panneAPI = {
    getAll: () => api.get<Panne[]>('/pannes'),
    getByStatut: (statut: StatutPanne) => api.get<Panne[]>('/pannes', {params: {statut}}),
    getByCamion: (camionId: number) => api.get<Panne[]>('/pannes', {params: {camionId}}),
    create: (p: Panne) => api.post<Panne>('/pannes', p),
    update: (id: number, p: Panne) => api.put<Panne>(`/pannes/${id}`, p),
    changerStatut: (id: number, statut: StatutPanne) => api.patch<Panne>(`/pannes/${id}/statut`, {statut}),
    delete: (id: number) => api.delete(`/pannes/${id}`),
};

// Documents Camion — visite technique, assurance, carte de transport
export const documentCamionAPI = {
    getAll: () => api.get<DocumentCamion[]>('/documents-camion'),
    getByCamion: (camionId: number) => api.get<DocumentCamion[]>('/documents-camion', {params: {camionId}}),
    getAlertes: () => api.get<DocumentCamion[]>('/documents-camion/alertes'),
    create: (d: DocumentCamion) => api.post<DocumentCamion>('/documents-camion', d),
    update: (id: number, d: DocumentCamion) => api.put<DocumentCamion>(`/documents-camion/${id}`, d),
    delete: (id: number) => api.delete(`/documents-camion/${id}`),
};

// Détention — conteneurs livrés dont le vide n'est pas restitué (2026-10-25)
export interface LigneDetention {
    conteneur: string; bl: string; client?: string; armateur?: string; dateLivraison: string;
    jours: number; depassement: number; etat: 'FRANCHISE' | 'DETENTION' | 'A_VERIFIER';
    recuperationStatut?: string; recuperationPrevue?: string;
    /** Parc INTERNE où le vide attend d'être renvoyé à l'armateur. */
    parcDepot?: string; dateDepot?: string;
}
export interface Detention {
    franchiseJours: number;
    compteurs: {chezClients: number; franchise: number; detention: number; aVerifier: number; surNosParcs: number};
    lignes: LigneDetention[];
    page: number; totalItems: number; totalPages: number;
}
export interface FiltresDetention { armateur?: string; etat?: string; recherche?: string }
const paramsDetention = (f: FiltresDetention) => ({
    armateur: f.armateur || undefined, etat: f.etat || undefined, recherche: f.recherche?.trim() || undefined,
});
export const detentionAPI = {
    /** @param page à partir de 0 */
    get: (filtres: FiltresDetention, page = 0, size = 25) =>
        api.get<Detention>('/recuperations/detention', {params: {...paramsDetention(filtres), page, size}}),
    excel: (filtres: FiltresDetention) =>
        api.get('/recuperations/detention/excel', {params: paramsDetention(filtres), responseType: 'blob'}),
};

// Rapports Logistique — livraisons/récupérations par zone/chauffeur/commis
export const rapportLogistiqueAPI = {
    get: (domaine: DomaineRapport, axe: AxeRapport, debut: string, fin: string) =>
        api.get<RapportLigne[]>('/rapports-logistique', {params: {domaine, axe, debut, fin}}),
    pdf: (domaine: DomaineRapport, axe: AxeRapport, debut: string, fin: string) =>
        api.get('/rapports-logistique/pdf', {params: {domaine, axe, debut, fin}, responseType: 'blob'}),
    xlsx: (domaine: DomaineRapport, axe: AxeRapport, debut: string, fin: string) =>
        api.get('/rapports-logistique/xlsx', {params: {domaine, axe, debut, fin}, responseType: 'blob'}),
};

// Service de notifications en temps réel
/**
 * Photos de mission.
 *
 * Elles etaient servies en clair sur /uploads/photos/**, sans authentification : toute photo
 * etait lisible par qui connaissait l'URL. Elles passent desormais par un endpoint authentifie,
 * qui verifie en outre l'appartenance de la mission.
 *
 * Une balise <img> ne sait pas porter d'en-tete Authorization : on recupere donc l'image par un
 * appel authentifie ordinaire — l'intercepteur ci-dessus y place le jeton — puis on l'affiche
 * depuis la memoire du navigateur.
 */
export const photoAPI = {
    /**
     * @param urlStockee URL telle qu'elle figure en base. Seul le nom de fichier est repris, ce
     *                   qui rend l'appel insensible au prefixe : les photos anterieures a la
     *                   correction sont stockees sous /uploads/photos/, les nouvelles sous
     *                   /api/photos/, et les deux s'affichent.
     */
    recuperer: (urlStockee: string) => {
        const nom = urlStockee.substring(urlStockee.lastIndexOf('/') + 1);
        return api.get<Blob>(`/photos/${encodeURIComponent(nom)}`, {responseType: 'blob'});
    },
};

export const notificationAPI = {
    // Connexion au flux SSE. Le jeton part dans l'en-tête Authorization, jamais dans l'URL :
    // celle-ci se retrouverait sinon dans les journaux d'accès, l'historique du navigateur et
    // tout en-tête Referer. La reconnexion reste reprise en main pour qu'elle porte le jeton
    // frais (cf. fluxSse.ts).
    ouvrirFlux: (gestionnaires: GestionnairesFluxSse) =>
        ouvrirFluxSse(() => `${API_BASE_URL}/notifications/stream`, gestionnaires),

    lister: () => api.get<NotificationApplicative[]>('/notifications'),
    marquerLu: (id: number) => api.put(`/notifications/${id}/lu`),
    marquerToutLu: () => api.put('/notifications/tout-lu'),

    // Récupérer le nombre de clients connectés
    getConnectedClients: () => api.get<number>("/notifications/clients"),
};

// Service dédié au chauffeur
export const chauffeurLivraisonAPI = {
    // Mise à jour du statut de livraison par le chauffeur
    updateStatut: (id: number, data: {
        statut: "EN_ATTENTE" | "EN_COURS" | "LIVREE";
        telephoneChauffeur: string;
        coordonneesGPS?: string;
        remarque?: string;
        recuperationConteneurVide?: boolean;
        conteneurVideReference?: string;
        positionRecuperation?: string;
    }) => api.post(`/chauffeur/livraisons/${id}/statut`, data),

    // Récupérer les livraisons assignées à un chauffeur
    getLivraisons: (telephone: string) => api.get<Livraison[]>(`/chauffeur/livraisons?telephone=${telephone}`),

    // Récupérer une livraison spécifique
    getLivraison: (id: number, telephone: string) => api.get<Livraison>(`/chauffeur/livraisons/${id}?telephone=${telephone}`),
    
    // ========== WORKFLOW COMPLET CHAUFFEUR ==========
    // Récupérer toutes les livraisons du chauffeur connecté
    getAll: (filtres?: FiltresLivraison) => {
        const params = new URLSearchParams();
        if (filtres?.statut) params.append('statut', filtres.statut);
        if (filtres?.type) params.append('type', filtres.type);
        if (filtres?.date) params.append('date', filtres.date);
        return api.get<ApiResponse<LivraisonWorkflow[]>>(`/chauffeur-workflow/livraisons?${params}`);
    },
    
    // Récupérer une livraison par ID
    getById: (id: number) => api.get<ApiResponse<LivraisonWorkflow>>(`/chauffeur-workflow/livraisons/${id}`),
    
    // Workflow complet
    accepter: (id: number) => api.post<ApiResponse<LivraisonWorkflow>>(`/chauffeur-workflow/livraisons/${id}/accepter`),
    refuser: (id: number, motif: string) => api.post<ApiResponse<LivraisonWorkflow>>(`/chauffeur-workflow/livraisons/${id}/refuser`, { motif }),
    enRoute: (id: number, position?: { latitude: number; longitude: number }) => 
        api.post<ApiResponse<LivraisonWorkflow>>(`/chauffeur-workflow/livraisons/${id}/en-route`, position),
    arrive: (id: number, position?: { latitude: number; longitude: number }) => 
        api.post<ApiResponse<LivraisonWorkflow>>(`/chauffeur-workflow/livraisons/${id}/arrive`, position),
    commencerLivraison: (id: number) => api.post<ApiResponse<LivraisonWorkflow>>(`/chauffeur-workflow/livraisons/${id}/commencer-livraison`),
    livrer: (id: number, data: { remarque?: string; photos?: PhotoLivraison[]; signature?: string }) => 
        api.post<ApiResponse<LivraisonWorkflow>>(`/chauffeur-workflow/livraisons/${id}/livrer`, data),
    terminer: (id: number) => api.post<ApiResponse<LivraisonWorkflow>>(`/chauffeur-workflow/livraisons/${id}/terminer`),
    
    // Upload photo
    uploadPhoto: (id: number, file: File, type: PhotoLivraison['type']) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        return api.post<ApiResponse<string>>(`/chauffeur-workflow/livraisons/${id}/photos/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

// ========== TRACKING GPS ==========
export const trackingAPI = {
    // Envoyer une position
    sendPosition: (data: Omit<TrackingGPS, 'id'>) => 
        api.post<ApiResponse<void>>('/chauffeur-workflow/tracking', data),
    
    // Envoyer un batch de positions
    sendPositionsBatch: (positions: Omit<TrackingGPS, 'id'>[]) => 
        api.post<ApiResponse<void>>('/chauffeur-workflow/tracking/batch', positions),
    
    // Récupérer l'historique d'une livraison
    getHistoriqueLivraison: (livraisonId: number) => 
        api.get<ApiResponse<TrackingGPS[]>>(`/chauffeur-workflow/tracking/livraison/${livraisonId}`),
    
    // Récupérer l'historique d'une récupération
    getHistoriqueRecuperation: (recuperationId: number) => 
        api.get<ApiResponse<TrackingGPS[]>>(`/chauffeur-workflow/tracking/recuperation/${recuperationId}`),
};

// ========== STATS CHAUFFEUR ==========
export const statsAPI = {
    // Récupérer les statistiques du chauffeur
    getStats: () => api.get<ApiResponse<StatsChauffeur>>('/chauffeur-workflow/stats'),
    
    // Récupérer les statistiques d'une période
    getStatsPeriode: (debut: string, fin: string) => 
        api.get<ApiResponse<StatsChauffeur>>('/chauffeur-workflow/stats', { params: { debut, fin } }),
};

// API Notifications Chauffeur
export const notificationChauffeurAPI = {
    // Créer une notification lors de la prise de rendez-vous
    creer: (livraisonId: number, telephoneChauffeur: string) =>
        api.post<NotificationChauffeur>('/notifications-chauffeur/creer', null, {
            params: {livraisonId, telephoneChauffeur}
        }),

    // Mettre à jour le statut par le chauffeur
    updateStatut: (id: number, data: UpdateNotificationStatutRequest) =>
        api.put<NotificationChauffeur>(`/notifications-chauffeur/${id}/statut`, data),

    // Récupérer les notifications d'un chauffeur
    getByChauffeur: (telephone: string) =>
        api.get<NotificationChauffeur[]>('/notifications-chauffeur/chauffeur', {
            params: {telephone}
        }),

    // Récupérer une notification par ID
    getById: (id: number) =>
        api.get<NotificationChauffeur>(`/notifications-chauffeur/${id}`),

    // Récupérer une notification par ID de livraison
    getByLivraisonId: (livraisonId: number) =>
        api.get<NotificationChauffeur>(`/notifications-chauffeur/livraison/${livraisonId}`),
};

// Conteneurs Au Parc
export const conteneurAuParcAPI = {
    getAll: () => api.get<ConteneurAuParc[]>('/conteneurs-au-parc'),
    getStats: () => api.get<Record<string, number>>('/conteneurs-au-parc/stats'),
    getPage: (page: number = 0, size: number = 25, params?: {
        typeTC?: string; operation?: string; status?: string; parcId?: number;
        dateDebut?: string; dateFin?: string; search?: string;
    }) => api.get('/conteneurs-au-parc/page', { params: { page, size, ...params } }),
    getLivrables: () => api.get<ConteneurAuParc[]>('/conteneurs-au-parc/livrables'),
    rechercherConteneur: (numeroConteneur: string) =>
        api.get<ConteneurAuParc>(`/conteneurs-au-parc/recherche/${numeroConteneur}`),
    rechercherConteneurs: (numerosConteneurs: string[]) =>
        api.post<ConteneurAuParc[]>('/conteneurs-au-parc/recherche-multiple', numerosConteneurs),
    /** @param bl le BL du cycle choisi ; sans lui, un numéro sur plusieurs dossiers est refusé. */
    verifierLivrable: (numeroConteneur: string, bl?: string) =>
        api.get<ConteneurLivrableDTO>(`/conteneurs-au-parc/verifier-livrable/${numeroConteneur}`,
            bl ? {params: {bl}} : undefined),
    enregistrer: (conteneur: ConteneurAuParc) =>
        api.post<ConteneurAuParc>('/conteneurs-au-parc', conteneur),
    getByTypeOperation: (typeOperation: string) =>
        api.get<ConteneurAuParc[]>(`/conteneurs-au-parc/type-operation/${typeOperation}`),
    getByNumBl: (numBl: string) =>
        api.get<ConteneurAuParc[]>(`/conteneurs-au-parc/bl/${numBl}`),
    isConteneurDejaLivre: (numeroConteneur: string) =>
        api.get<boolean>(`/conteneurs-au-parc/est-livre/${numeroConteneur}`),
    isConteneurPleinEnregistre: (numeroConteneur: string, typeOperation: string) =>
        api.get<boolean>(`/conteneurs-au-parc/existe/${numeroConteneur}/${typeOperation}`),
};

// Gestion des Conteneurs au Parc (nouveau système avec états)
export interface EntreeParcRequest {
    numeroConteneur: string;
    estVide: boolean;
    chauffeur: string;
    numCamion: string;
    numBl?: string;
    parcId?: number;
    parcNom?: string;
}

export interface SortieParcRequest {
    numeroConteneur: string;
    destination: string;
    chauffeur: string;
    numCamion: string;
    versClient: boolean;
}

export interface DepotageRequest {
    numeroConteneur: string;
    commentaire?: string;
    utilisateur?: string;
    roleUtilisateur?: string;
}

export interface LivraisonClientRequest {
    numeroConteneur: string;
    commentaire?: string;
}

// Note: HistoriqueConteneur est importé depuis ../types
// Utiliser l'interface définie dans types/index.ts pour la cohérence

export const gestionConteneurParcAPI = {
    // Entrée d'un conteneur au parc
    enregistrerEntree: (request: EntreeParcRequest) =>
        api.post<ConteneurAuParc>('/conteneurs-parc/entree', request),

    // Sortie d'un conteneur du parc
    enregistrerSortie: (request: SortieParcRequest) =>
        api.post<ConteneurAuParc>('/conteneurs-parc/sortie', request),

    // Dépotage d'un conteneur dans le parc
    enregistrerDepotage: (request: DepotageRequest) =>
        api.post<ConteneurAuParc>('/conteneurs-parc/depotage', request),

    // Confirmation livraison client
    confirmerLivraison: (request: LivraisonClientRequest) =>
        api.post<ConteneurAuParc>('/conteneurs-parc/livraison-client', request),

    // Récupérer les conteneurs par état
    getParEtat: (etat: string) =>
        api.get<ConteneurAuParc[]>(`/conteneurs-parc/par-etat/${etat}`),

    // Récupérer tous les conteneurs au parc
    getAuParc: () =>
        api.get<ConteneurAuParc[]>('/conteneurs-parc/au-parc'),

    // Récupérer l'historique d'un conteneur
    getHistorique: (numeroConteneur: string) =>
        api.get<HistoriqueConteneur[]>(`/conteneurs-parc/${numeroConteneur}/historique`),

    // Vérifier si un conteneur peut être dépoté
    peutDepoter: (numeroConteneur: string) =>
        api.get<boolean>(`/conteneurs-parc/${numeroConteneur}/peut-depoter`),

    // Vérifier si un conteneur est disponible pour sortie
    estDisponibleSortie: (numeroConteneur: string) =>
        api.get<boolean>(`/conteneurs-parc/${numeroConteneur}/disponible-sortie`),
};

// Gestion des Utilisateurs (Admin uniquement)
// Endpoints alignés avec AuthController.java
export const userAPI = {
    // Récupérer tous les utilisateurs
    getAll: () => api.get<Utilisateur[]>('/auth/users'),

    // Récupérer un utilisateur par ID
    getById: (id: number) => api.get<Utilisateur>(`/auth/users/${id}`),

    // Créer un nouvel utilisateur (compte créé dans Keycloak, source de vérité de l'authentification)
    // POST /api/auth/users — mot de passe dans le corps de la requête, jamais en query string
    // (une query string finit en clair dans les logs d'accès/proxy).
    create: (user: CreateUserRequest & { modules?: string[] }) => {
        const { password, modules, ...userDTO } = user;
        return api.post<Utilisateur>('/auth/users', { utilisateur: userDTO, password, modules });
    },

    // Modules d'un utilisateur
    getModules:    (id: number) => api.get<{ modules: string[] }>(`/auth/users/${id}/modules`),
    updateModules: (id: number, modules: string[]) => api.put(`/auth/users/${id}/modules`, { modules }),

    // Mettre à jour un utilisateur
    // PUT /api/auth/users/{id} avec body: UtilisateurDTO
    update: (id: number, user: UpdateUserRequest) => api.put<Utilisateur>(`/auth/users/${id}`, user),

    // Activer ou desactiver un compte. Ne touche QUE l etat actif : ni e-mail, ni role, ni
    // modules — update() ci-dessus les reecrit, et exige un e-mail que certains comptes n ont pas.
    changerEtat: (id: number, actif: boolean) =>
        api.put<Utilisateur>(`/auth/users/${id}/actif`, {actif}),

    // Supprimer un utilisateur
    // DELETE /api/auth/users/{id}
    delete: (id: number) => api.delete(`/auth/users/${id}`),

    // Réinitialiser le mot de passe d'un utilisateur (action administrateur via Keycloak)
    // POST /api/auth/users/{id}/reset-password — mot de passe dans le corps, pas en query string.
    changePassword: (id: number, passwordData: ChangePasswordRequest) =>
        api.post(`/auth/users/${id}/reset-password`, { newPassword: passwordData.newPassword }),

    // Activer/Désactiver un utilisateur (via update)
    // PUT /api/auth/users/{id} avec body: { actif: boolean }
    // Endpoint DEDIE a l etat actif. L interrupteur de la liste appelait PUT /auth/users/{id} avec
    // le seul champ actif : cet endpoint valide un utilisateur COMPLET, il repondait donc 400 pour
    // tous les comptes — l interrupteur n a jamais fonctionne. Si la validation etait passee, il
    // aurait recopie ce corps presque vide dans le compte : nom, e-mail et role remis a vide.
    toggleStatus: (id: number, actif: boolean) =>
        api.put<Utilisateur>(`/auth/users/${id}/actif`, { actif }),

    // Récupérer l'historique des actions (à implémenter côté backend si nécessaire)
    getLogs: () => api.get<UserLog[]>('/auth/users/logs'),

    // Récupérer les logs d'un utilisateur spécifique
    getLogsByUserId: (userId: number) => api.get<UserLog[]>(`/auth/users/${userId}/logs`),
};

export default api;
